import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  TrendingUp,
  Wallet,
  Receipt,
  FileBarChart,
  Banknote,
  Network,
  Layers,
  ArrowLeftRight,
  Combine,
  Building2,
  Contact,
  ListOrdered,
  Clock,
  SlidersHorizontal,
  CalendarClock,
  Percent,
  PieChart,
  FlaskConical,
  ShieldCheck,
  LogOut,
  Upload,
  History,
  CalendarCheck,
  HandCoins,
  Menu,
  X,
} from "lucide-react";
import { useAuth, ROLE_LABELS } from "../../context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const NAV = [
  {
    items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true }],
  },
  {
    label: "Planning",
    items: [
      { to: "/planning/resource-planning", label: "Resource Planning", icon: Users },
      { to: "/planning/revenue", label: "Revenue", icon: TrendingUp },
      { to: "/planning/personnel-costs", label: "Personnel Costs", icon: Wallet },
      { to: "/planning/indirect-costs", label: "Indirect Costs", icon: Receipt },
      { to: "/planning/scenarios", label: "Scenarios", icon: FlaskConical },
      { to: "/planning/dividends", label: "Dividends", icon: HandCoins },
    ],
  },
  {
    label: "Financials",
    items: [
      { to: "/financials/pnl", label: "P&L", icon: FileBarChart },
      { to: "/financials/cash-flow", label: "Cash Flow", icon: Banknote },
    ],
  },
  {
    label: "Actuals",
    items: [
      { to: "/actuals/import", label: "Import P&L", icon: Upload },
      { to: "/actuals/history", label: "Import History", icon: History },
      { to: "/actuals/period-close", label: "Period Close", icon: CalendarCheck },
    ],
  },
  {
    label: "Group",
    items: [
      { to: "/group/overview", label: "Group Overview", icon: Network },
      { to: "/group/overhead", label: "Group Overhead", icon: Layers },
      { to: "/group/intercompany", label: "Intercompany", icon: ArrowLeftRight },
      { to: "/group/consolidation", label: "Consolidation", icon: Combine },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/admin/companies", label: "Companies", icon: Building2 },
      { to: "/admin/people", label: "People", icon: Contact },
      { to: "/admin/customers", label: "Customers", icon: Users },
      { to: "/admin/cost-accounts", label: "Cost Accounts", icon: ListOrdered },
      { to: "/admin/working-hours", label: "Working Hours", icon: Clock },
      { to: "/admin/financial-parameters", label: "Financial Parameters", icon: SlidersHorizontal },
      { to: "/admin/payment-terms", label: "Payment Terms", icon: CalendarClock },
      { to: "/admin/vat", label: "VAT", icon: Percent },
      { to: "/admin/ownership", label: "Ownership", icon: PieChart },
      { to: "/admin/accounts", label: "Accounts & Permissions", icon: ShieldCheck },
    ],
  },
];

function initials(name = "") {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function Brand({ testId }) {
  return (
    <div className="h-16 border-b border-border flex items-center px-4 shrink-0">
      <img src="/elva-logo-blue.png" alt="Elva Group" className="h-6 w-auto" data-testid={testId} />
      <span className="ml-3 border-l border-border pl-2.5 text-[11px] font-medium text-muted-foreground">
        Financial Planning
      </span>
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-secondary transition-colors duration-150" data-testid="user-menu-button">
          <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
            {initials(user?.name)}
          </div>
          <div className="text-left">
            <div className="text-sm font-medium text-foreground leading-tight">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground leading-tight">{ROLE_LABELS[user?.role] || user?.role}</div>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem
          data-testid="logout-button"
          onClick={() => {
            logout();
            navigate("/login");
          }}
        >
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NavLinks({ onNavigate }) {
  return (
    <nav className="flex-1 overflow-y-auto elva-scroll py-4 px-3 space-y-5">
      {NAV.map((group, gi) => (
        <div key={gi}>
          {group.label && (
            <div className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {group.label}
            </div>
          )}
          <div className="space-y-0.5">
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onNavigate}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-[color,background-color,transform] duration-150 hover:translate-x-0.5 ${
                    isActive
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`
                }
              >
                <item.icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

export default function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background" data-testid="app-shell">
      {/* Mobile top bar (sidebar hidden below lg) */}
      <header className="lg:hidden bg-white border-b border-border sticky top-0 z-40 h-16 px-4 flex items-center justify-between">
        <div className="flex items-center min-w-0">
          <button
            className="inline-flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors duration-150 mr-2 shrink-0"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-button"
          >
            <Menu className="h-5 w-5" strokeWidth={1.8} />
          </button>
          <img src="/elva-logo-blue.png" alt="Elva Group" className="h-6 w-auto" data-testid="mobile-header-logo" />
          <span className="ml-3 border-l border-border pl-2.5 text-[11px] font-medium text-muted-foreground truncate">
            Financial Planning
          </span>
        </div>
        <UserMenu />
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-overlay" />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white border-r border-border flex flex-col" data-testid="mobile-sidebar">
            <button
              className="absolute top-4 right-3 inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors duration-150 z-10"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X className="h-5 w-5" strokeWidth={1.8} />
            </button>
            <Brand testId="mobile-drawer-logo" />
            <NavLinks onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex">
        {/* Full-height sidebar: the vertical border runs from the very top of
            the page, with the brand above the menu (CRM layout). */}
        <aside
          className="hidden lg:flex flex-col bg-white border-r border-border w-64 shrink-0 sticky top-0 h-screen"
          data-testid="sidebar"
        >
          <Brand testId="header-logo" />
          <NavLinks />
        </aside>
        <div className="flex-1 min-w-0 flex flex-col">
          <header className="hidden lg:flex bg-white border-b border-border sticky top-0 z-40 h-16 px-4 md:px-6 items-center justify-end">
            <UserMenu />
          </header>
          <main className="flex-1 p-4 md:p-8 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
